import {
  EditProduct,
  EditProductAttribute,
  GetProdutcsResult,
  NewProduct,
  NewProductAttribute,
  Product,
  Size,
  Sort,
} from "../../domain";
import { logger } from "../../utils";
import {
  Endpoints,
  RemoteDataSource,
  ProductsRemoteDataSourceError,
} from "../core";

export class ProductsRemoteDataSource extends RemoteDataSource {
  public async getProductInfo(id: string): Promise<Product> {
    try {
      const res = await this.get(Endpoints.productInfo, { id });
      return new Product().deserialize(res.data);
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      if (error.response.status === 404)
        throw ProductsRemoteDataSourceError.productNotFoundError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async getSizes(): Promise<Size[]> {
    try {
      const res = await this.get(Endpoints.sizesList);
      return res.data.map((json: any) => new Size().deserialize(json));
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async getProducts(
    page: number,
    sort: Sort
  ): Promise<GetProdutcsResult> {
    try {
      const res = await this.get(Endpoints.listProducts, { page, sort });
      return {
        products: res.data.products.map((json: any) =>
          new Product().deserialize(json)
        ),
        productsCount: res.data.count,
        pageSize: res.data.page_size,
      };
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      if (error.response.status === 404)
        throw ProductsRemoteDataSourceError.categoryNotFoundError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async add(product: NewProduct): Promise<void> {
    try {
      const {
        title,
        description,
        categoryId,
        subCategoryId,
        generalCategoryId,
        images,
        attributes,
        label,
      } = product;

      const body = new FormData();
      body.append("title", title);
      body.append("description", description);
      body.append("label", label === undefined ? "" : label);
      body.append(
        "attributes",
        JSON.stringify(
          attributes.map((attribute) => {
            const { color, count, price, size, sizeDescription } = attribute;
            return {
              color: color.id,
              count,
              price,
              size,
              size_description: sizeDescription,
            };
          })
        )
      );
      if (generalCategoryId !== undefined)
        body.append("general_category", generalCategoryId);
      if (subCategoryId !== undefined)
        body.append("sub_category", subCategoryId);
      if (categoryId !== undefined) body.append("category", categoryId);
      for (let index = 0; index < images.length; index++) {
        const image = images[index];
        body.append(image.index.toString(), image.file);
      }

      await this.post(Endpoints.addProduct, {
        body: body,
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (error) {
      logger.error(error);
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async remove(id: string): Promise<void> {
    try {
      await this.post(Endpoints.removeProduct, { body: { id } });
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async editProductInfo(product: EditProduct): Promise<void> {
    try {
      const {
        id,
        title,
        description,
        generalCategoryId,
        subCategoryId,
        categoryId,
        active,
        label,
      } = product;
      await this.post(Endpoints.editProduct, {
        body: {
          product_id: id,
          description: description === undefined ? "" : description,
          label: label === undefined ? "" : label,
          title: title === undefined ? "" : title,
          general_category:
            generalCategoryId === undefined ? null : generalCategoryId,
          sub_category: subCategoryId === undefined ? null : subCategoryId,
          category: categoryId === undefined ? null : categoryId,
          active: active === undefined ? "" : active,
        },
      });
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async editProductAttribute(attribute: EditProductAttribute) {
    try {
      const {
        id,
        color,
        count,
        price,
        offPrice,
        size,
        sizeDescription,
      } = attribute;
      await this.post(Endpoints.editProduct, {
        body: {
          attribute_id: id,
          size: size === undefined ? "" : size,
          color: color === undefined ? "" : color,
          price: price === undefined ? "" : price,
          ...(offPrice === undefined ? {} : { off_price: offPrice }),
          size_description:
            sizeDescription === undefined ? "" : sizeDescription,
          count: count === undefined ? "" : count,
        },
      });
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async addAttributeToProduct(
    productId: string,
    attribute: NewProductAttribute
  ) {
    try {
      const { color, count, price, size, sizeDescription } = attribute;
      await this.post(Endpoints.addAttributeToProduct, {
        body: {
          product_id: productId,
          size,
          color: color.id.toString(),
          price,
          size_description: sizeDescription,
          count,
        },
      });
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async setProductAttributesPrice(
    productId: string,
    price: number
  ): Promise<void> {
    try {
      await this.patch(Endpoints.setProductAttributesPrice, {
        body: { id: productId, price },
      });
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async setProductAttributesOffPrice(
    productId: string,
    offPrice: number
  ): Promise<void> {
    try {
      await this.patch(Endpoints.setProductAttributesOffPrice, {
        body: { id: productId, off_price: offPrice },
      });
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async addImageToProduct(
    productId: string,
    image: File,
    index: number
  ): Promise<void> {
    try {
      const body = new FormData();
      body.append("product_id", productId);
      body.append("image", image);
      body.append("index", index.toString());
      await this.patch(Endpoints.addImageToProduct, { body });
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async deleteImageFromProduct(imageId: string): Promise<void> {
    try {
      await this.post(Endpoints.deleteImageFromProduct, {
        body: { image_id: imageId },
      });
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }

  public async editProductImage(imageId: string, image: File): Promise<void> {
    try {
      const body = new FormData();
      body.append("image_id", imageId);
      body.append("image", image);
      await this.patch(Endpoints.editProductImage, { body });
    } catch (error) {
      if (!error.response) throw ProductsRemoteDataSourceError.networkError();
      throw ProductsRemoteDataSourceError.serverError();
    }
  }
}
